import type { Editor } from "@tiptap/react";

import * as React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { HotkeyIndicator } from "@replo/design-system/components/hotkey-indicator/HotkeyIndicator";
import {
  TooltipContent,
  TooltipRoot,
  TooltipTrigger,
} from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { Mic } from "lucide-react";

import { useVoiceInput } from "../../../hooks/useVoiceInput";

interface VoiceInputButtonProps {
  editor: Editor | null;
}

export interface VoiceInputButtonRef {
  stopRecording: () => void;
  toggleRecording: () => void;
}

export const VoiceInputButton = React.forwardRef<
  VoiceInputButtonRef,
  VoiceInputButtonProps
>(function VoiceInputButton({ editor }, ref) {
  const logEvent = useLogAnalytics();
  const startPositionRef = React.useRef<number | null>(null);
  const transcriptLengthRef = React.useRef(0);
  const isMountedRef = React.useRef(true);

  React.useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const {
    isRecording,
    isSupported,
    interimTranscript,
    startRecording: startRecordingInternal,
    stopRecording: stopRecordingInternal,
    error,
  } = useVoiceInput({
    onTranscriptUpdate: (text) => {
      if (
        !editor ||
        !isMountedRef.current ||
        startPositionRef.current === null
      ) {
        return;
      }

      const trimmedText = text.trim();

      const endPosition = startPositionRef.current! + trimmedText.length;

      editor
        .chain()
        .focus()
        .command(({ tr }) => {
          if (transcriptLengthRef.current > 0) {
            tr.delete(
              startPositionRef.current!,
              startPositionRef.current! + transcriptLengthRef.current,
            );
          }
          if (trimmedText) {
            tr.insertText(trimmedText, startPositionRef.current!);
          }
          return true;
        })
        .setTextSelection(endPosition)
        .run();

      transcriptLengthRef.current = trimmedText.length;
    },
    onTranscriptComplete: (transcription) => {
      logEvent("chat.voice.complete", { transcription });
    },
  });

  const startRecording = React.useCallback(() => {
    if (!editor) {
      return;
    }

    logEvent("chat.voice.start");

    // Focus the editor but keep cursor at current position
    editor.commands.focus();

    const { from } = editor.state.selection;

    // Check if we need a space by looking at the character before the cursor
    // We need a space if: cursor is not at start AND the character before cursor is not a space/newline
    let needsSpace = false;
    if (from > 1) {
      const textBefore = editor.state.doc.textBetween(from - 1, from, "\n");
      needsSpace = textBefore.length > 0 && !/\s/.test(textBefore);
    }

    if (needsSpace) {
      // Use transaction API to insert space directly (avoids creating new paragraphs)
      editor
        .chain()
        .command(({ tr }) => {
          tr.insertText(" ", from);
          return true;
        })
        .run();
      startPositionRef.current = from + 1;
    } else {
      startPositionRef.current = from;
    }

    transcriptLengthRef.current = 0;
    startRecordingInternal();
  }, [editor, logEvent, startRecordingInternal]);

  const stopRecording = React.useCallback(() => {
    if (isRecording) {
      stopRecordingInternal();
      startPositionRef.current = null;
      transcriptLengthRef.current = 0;
    }
  }, [isRecording, stopRecordingInternal]);

  const toggleRecording = React.useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [isRecording, stopRecording, startRecording]);

  React.useImperativeHandle(
    ref,
    () => ({
      stopRecording,
      toggleRecording,
    }),
    [stopRecording, toggleRecording],
  );

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleRecording();
  };

  const tooltipContent = (() => {
    if (!isSupported) {
      return "Voice input is not supported in this browser";
    }
    if (error) {
      return error;
    }
    if (isRecording) {
      return interimTranscript
        ? `"${interimTranscript}"`
        : "Listening... Click to stop";
    }
    return <HotkeyIndicator hotkey="toggleVoice" />;
  })();

  const isDisabled = !isSupported || Boolean(error);

  if (!isSupported) {
    return null;
  }

  return (
    <TooltipRoot disableHoverablePopup>
      <TooltipTrigger
        render={
          <span>
            <Button
              icon
              variant={isRecording ? "destructive" : "secondary"}
              size="base"
              disabled={isDisabled}
              onClick={handleClick}
              aria-label={
                isRecording ? "Stop voice input" : "Start voice input"
              }
            >
              <div className="relative">
                <Mic
                  className={twMerge(
                    "h-4 w-4 transition-colors duration-200",
                    !isSupported && "opacity-50",
                  )}
                />
                {isRecording && (
                  <span
                    className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-rose animate-pulse"
                    aria-hidden="true"
                  />
                )}
              </div>
            </Button>
          </span>
        }
      />
      <TooltipContent>{tooltipContent}</TooltipContent>
    </TooltipRoot>
  );
});
